import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  PhoneCall,
  PhoneOff,
  SwitchCamera,
  Copy,
  Check,
  QrCode,
  ShieldCheck,
  ShieldAlert,
  Info,
  Clock,
  Radio,
  RefreshCw,
  AlertTriangle,
  Volume2,
  Sparkles,
  Maximize2,
  Layers,
  ArrowRight,
  Wifi,
  Smartphone,
  Camera,
  Sliders,
  Settings,
  ExternalLink,
  Users
} from 'lucide-react';
import QRCode from 'qrcode';
import { webrtcMeetingService } from '@/services/webrtcMeetingService';
import { WebRTCConnectionState } from '@/types';
import { formatDuration } from '@/utils/helpers';
import { PwaInstallButton } from '@/components/PwaInstallButton';

export interface MobileGuestMeetingScreenProps {
  isMobileGuestHub?: boolean;
  activeGuestTab?: 'group' | 'podcast';
  onSwitchGuestTab?: (tab: 'group' | 'podcast') => void;
}

export const MobileGuestMeetingScreen: React.FC<MobileGuestMeetingScreenProps> = ({
  isMobileGuestHub,
  activeGuestTab,
  onSwitchGuestTab
}) => {
  // Detect if running on mobile phone vs desktop PC / Chrome Extension
  const isMobileDevice = typeof navigator !== 'undefined' && (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (typeof window !== 'undefined' && 'ontouchstart' in window && navigator.maxTouchPoints > 1 && window.innerWidth < 768)
  );

  // Mobile camera facingMode (user = front, environment = back)
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [isFlippingCamera, setIsFlippingCamera] = useState(false);

  // Desktop / Multi-device state
  const [videoDevices, setVideoDevices] = useState<{ deviceId: string; label: string }[]>([]);
  const [audioDevices, setAudioDevices] = useState<{ deviceId: string; label: string }[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string>('');
  const [selectedMicId, setSelectedMicId] = useState<string>('');

  // Streams
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  useEffect(() => {
    localStreamRef.current = localStream;
  }, [localStream]);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);

  // Mute states
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [remoteMediaState, setRemoteMediaState] = useState<{ audioMuted: boolean; videoOff: boolean }>({
    audioMuted: false,
    videoOff: false
  });

  // Signaling & Connection
  const [signalingMode, setSignalingMode] = useState<'anydesk' | 'manual'>('anydesk');
  const [meetingCode, setMeetingCode] = useState(() => {
    if (typeof window !== 'undefined') {
      const codeParam = new URLSearchParams(window.location.search).get('code');
      if (codeParam) return codeParam.replace(/\D/g, '').slice(0, 6);
    }
    return '';
  });
  const [guestDisplayName, setGuestDisplayName] = useState('Remote Guest');
  const [hostDisplayName, setHostDisplayName] = useState('Podcast Host');
  const [connectionStatus, setConnectionStatus] = useState<WebRTCConnectionState>('idle');
  const [callDurationSec, setCallDurationSec] = useState<number>(0);
  const [rttLatencyMs, setRttLatencyMs] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Manual / QR code fallback
  const [inputOfferCode, setInputOfferCode] = useState('');
  const [generatedAnswerCode, setGeneratedAnswerCode] = useState('');
  const [copiedAnswer, setCopiedAnswer] = useState(false);
  const [qrModalPayload, setQrModalPayload] = useState<string | null>(null);

  // Consent & Audio Autoplay
  const [consentRequestModal, setConsentRequestModal] = useState<{ hostName: string } | null>(null);
  const [consentGranted, setConsentGranted] = useState(false);
  const [isAudioAutoplayBlocked, setIsAudioAutoplayBlocked] = useState(false);
  const [isHostRecording, setIsHostRecording] = useState(false);

  // Video Layout Swap (Primary vs PIP)
  const [primaryView, setPrimaryView] = useState<'remote' | 'local'>('remote');

  // DOM Refs
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const hostVideoRef = useRef<HTMLVideoElement | null>(null);
  const guestVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const qrCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const timerRef = useRef<any>(null);

  // Enumerate devices helper with auto-detection of connected / disconnected hardware
  const enumerateDevicesList = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return { vDevs: [], aDevs: [] };
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const vDevs = devices
        .filter((d) => d.kind === 'videoinput')
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` }));
      const aDevs = devices
        .filter((d) => d.kind === 'audioinput')
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Microphone ${i + 1}` }));

      setVideoDevices(vDevs);
      setAudioDevices(aDevs);

      // Auto-detect and set defaults: if previous device was unplugged or empty, select first available
      setSelectedCameraId((prev) => {
        if (prev && vDevs.some(d => d.deviceId === prev)) return prev;
        return vDevs[0]?.deviceId ?? '';
      });

      setSelectedMicId((prev) => {
        if (prev && aDevs.some(d => d.deviceId === prev)) return prev;
        return aDevs[0]?.deviceId ?? '';
      });

      return { vDevs, aDevs };
    } catch (err) {
      console.warn('[Devices] Enumerate error:', err);
      return { vDevs: [], aDevs: [] };
    }
  }, []);

  // Bulletproof Media Stream Acquisition with Auto-detection
  const acquireStream = useCallback(
    async (
      targetFacing: 'user' | 'environment',
      targetCameraId?: string,
      targetMicId?: string
    ): Promise<MediaStream> => {
      // 1. First probe if any camera device is available
      let hasAvailableCamera = true;
      if (!isMobileDevice) {
        try {
          const devList = await navigator.mediaDevices.enumerateDevices();
          const cams = devList.filter(d => d.kind === 'videoinput');
          if (cams.length === 0) {
            hasAvailableCamera = false;
          }
        } catch {}
      }

      // 2. Build audio constraints
      const audioConstraint: boolean | MediaTrackConstraints = targetMicId
        ? { deviceId: { exact: targetMicId } }
        : { echoCancellation: true, noiseSuppression: true, autoGainControl: true };

      // 3. If no camera is connected, immediately acquire audio-only stream cleanly
      if (!hasAvailableCamera) {
        setIsVideoMuted(true);
        const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraint });
        return stream;
      }

      // 4. Camera is detected -> build video constraints
      let videoConstraint: boolean | MediaTrackConstraints = true;
      if (isMobileDevice) {
        videoConstraint = {
          facingMode: { ideal: targetFacing },
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 }
        };
      } else {
        videoConstraint = targetCameraId
          ? { deviceId: { exact: targetCameraId } }
          : { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } };
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraint,
          audio: audioConstraint
        });
        setIsVideoMuted(false);
        return stream;
      } catch (primaryErr: any) {
        // Fallback: standard video & audio without strict bounds
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: audioConstraint });
          setIsVideoMuted(false);
          return stream;
        } catch {
          // Fallback to audio-only if camera is not accessible or in use
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraint });
            setIsVideoMuted(true);
            return stream;
          } catch {
            throw primaryErr;
          }
        }
      }
    },
    [isMobileDevice]
  );

  // Initialize Camera on Mount or on Permission Request
  const requestMediaPermissions = useCallback(async () => {
    setPermissionError(null);
    setErrorMessage(null);

    try {
      const stream = await acquireStream(facingMode, selectedCameraId, selectedMicId);
      setLocalStream(stream);
      setHasPermission(true);

      if (previewVideoRef.current && stream.getVideoTracks().length > 0) {
        previewVideoRef.current.srcObject = stream;
      }

      // Enumerate devices now that permission is granted (to get real device names)
      await enumerateDevicesList();
    } catch (err: any) {
      setHasPermission(false);
      setPermissionError(
        err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError' || err.message?.includes('dismissed')
          ? 'Camera and Microphone permissions were denied or dismissed. Click below to allow access.'
          : err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError'
          ? 'No camera or microphone found on this device.'
          : 'Could not access camera or microphone. Please check permissions.'
      );
    }
  }, [acquireStream, facingMode, selectedCameraId, selectedMicId, enumerateDevicesList]);

  // Initial mount trigger
  useEffect(() => {
    let mounted = true;
    requestMediaPermissions().then(() => {
      if (!mounted && localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
      }
    });

    return () => {
      mounted = false;
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
      }
      try {
        webrtcMeetingService.disconnect();
      } catch {}
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Auto-detect newly plugged in or disconnected hardware (Camera & Mic hot-plugging)
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.addEventListener) return;

    const handleDeviceChange = async () => {
      const { vDevs } = await enumerateDevicesList();

      const currentVideoTrack = localStreamRef.current?.getVideoTracks()[0];
      const hadLiveVideo = Boolean(currentVideoTrack && currentVideoTrack.readyState === 'live');

      if (!hadLiveVideo && vDevs.length > 0) {
        // USB Camera was plugged in! Auto-activate camera
        try {
          const newStream = await acquireStream(facingMode, vDevs[0].deviceId, selectedMicId);
          setLocalStream(newStream);
          webrtcMeetingService.updateLocalStream(newStream);
          if (previewVideoRef.current) previewVideoRef.current.srcObject = newStream;
          if (guestVideoRef.current) guestVideoRef.current.srcObject = newStream;
        } catch {}
      } else if (hadLiveVideo && vDevs.length === 0) {
        // Camera was unplugged! Auto-switch to audio-only mode
        try {
          const newStream = await acquireStream(facingMode, '', selectedMicId);
          setLocalStream(newStream);
          webrtcMeetingService.updateLocalStream(newStream);
        } catch {}
      }
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [enumerateDevicesList, acquireStream, facingMode, selectedMicId]);

  // Hardware watchdog: listen to track onended (physical disconnect of webcam/mic)
  useEffect(() => {
    if (!localStream) return;
    const vTrack = localStream.getVideoTracks()[0];
    if (vTrack) {
      const handleEnded = () => {
        enumerateDevicesList();
        setIsVideoMuted(true);
      };
      vTrack.addEventListener('ended', handleEnded);
      return () => vTrack.removeEventListener('ended', handleEnded);
    }
  }, [localStream, enumerateDevicesList]);

  // Update preview video srcObject whenever localStream changes
  useEffect(() => {
    if (previewVideoRef.current && localStream && localStream.getVideoTracks().length > 0) {
      previewVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // WebRTC Service Subscriptions
  useEffect(() => {
    webrtcMeetingService.setEvents({
      onStatusChange: (status) => {
        setConnectionStatus(status);
        if (status === 'connected') {
          setErrorMessage(null);
          if (timerRef.current) clearInterval(timerRef.current);
          setCallDurationSec(0);
          timerRef.current = setInterval(() => {
            setCallDurationSec((prev) => prev + 1);
          }, 1000);
        } else if (status === 'disconnected' || status === 'failed') {
          setRemoteStream(null);
          setConsentGranted(false);
          setIsHostRecording(false);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
      },
      onRemoteStream: (stream) => {
        console.log('[Guest] Remote stream received from Host:', stream.getTracks());
        setRemoteStream(stream);
      },
      onRecordingConsentRequest: (hName) => {
        setHostDisplayName(hName || 'Podcast Host');
        setConsentRequestModal({ hostName: hName || 'Podcast Host' });
      },
      onRecordingConsentResponse: (granted) => {
        setConsentGranted(granted);
      },
      onRecordingStarted: () => {
        setIsHostRecording(true);
      },
      onRecordingStopped: () => {
        setIsHostRecording(false);
      },
      onRemoteMediaStateChange: (state) => {
        setRemoteMediaState(state);
      },
      onLatencyUpdate: (rtt) => {
        setRttLatencyMs(rtt);
      },
      onError: (msg) => {
        setErrorMessage(msg);
      }
    });

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      webrtcMeetingService.disconnect();
    };
  }, []);

  // Sync remote stream into video & audio elements
  useEffect(() => {
    if (remoteStream) {
      if (hostVideoRef.current) {
        hostVideoRef.current.srcObject = remoteStream;
      }
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = remoteStream;
        remoteAudioRef.current.play().catch(() => {
          setIsAudioAutoplayBlocked(true);
        });
      }
    }
  }, [remoteStream, primaryView]);

  // Sync local stream into in-call guest video element
  useEffect(() => {
    if (localStream && guestVideoRef.current) {
      guestVideoRef.current.srcObject = localStream;
    }
  }, [localStream, primaryView, connectionStatus]);

  // Draw QR code if modal open
  useEffect(() => {
    if (qrModalPayload && qrCanvasRef.current) {
      QRCode.toCanvas(qrCanvasRef.current, qrModalPayload, {
        width: 240,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' }
      }).catch(console.error);
    }
  }, [qrModalPayload]);

  // Switch camera on Mobile (Front <-> Back)
  const handleFlipMobileCamera = async () => {
    if (isFlippingCamera) return;
    setIsFlippingCamera(true);

    const nextFacing = facingMode === 'user' ? 'environment' : 'user';

    try {
      if (localStream) {
        localStream.getVideoTracks().forEach((t) => t.stop());
      }

      const newStream = await acquireStream(nextFacing);

      // Preserve mute state
      const audioTrack = newStream.getAudioTracks()[0];
      if (audioTrack) audioTrack.enabled = !isAudioMuted;
      const videoTrack = newStream.getVideoTracks()[0];
      if (videoTrack) videoTrack.enabled = !isVideoMuted;

      setFacingMode(nextFacing);
      setLocalStream(newStream);

      webrtcMeetingService.updateLocalStream(newStream);

      if (previewVideoRef.current) previewVideoRef.current.srcObject = newStream;
      if (guestVideoRef.current) guestVideoRef.current.srcObject = newStream;
    } catch (err: any) {
      console.warn('[Guest] Camera flip failed:', err);
      setErrorMessage('Could not switch mobile camera.');
    } finally {
      setIsFlippingCamera(false);
    }
  };

  // Switch camera on Desktop PC (select specific deviceId)
  const handleSelectDesktopCamera = async (deviceId: string) => {
    setSelectedCameraId(deviceId);
    try {
      if (localStream) {
        localStream.getVideoTracks().forEach((t) => t.stop());
      }

      const newStream = await acquireStream(facingMode, deviceId, selectedMicId);

      const audioTrack = newStream.getAudioTracks()[0];
      if (audioTrack) audioTrack.enabled = !isAudioMuted;
      const videoTrack = newStream.getVideoTracks()[0];
      if (videoTrack) videoTrack.enabled = !isVideoMuted;

      setLocalStream(newStream);
      webrtcMeetingService.updateLocalStream(newStream);

      if (previewVideoRef.current) previewVideoRef.current.srcObject = newStream;
      if (guestVideoRef.current) guestVideoRef.current.srcObject = newStream;
    } catch (err: any) {
      console.warn('[Guest] Failed to switch camera device:', err);
      setErrorMessage('Unable to activate selected camera.');
    }
  };

  // Switch microphone on Desktop PC (select specific deviceId)
  const handleSelectDesktopMic = async (deviceId: string) => {
    setSelectedMicId(deviceId);
    try {
      if (localStream) {
        localStream.getAudioTracks().forEach((t) => t.stop());
      }

      const newStream = await acquireStream(facingMode, selectedCameraId, deviceId);

      const audioTrack = newStream.getAudioTracks()[0];
      if (audioTrack) audioTrack.enabled = !isAudioMuted;
      const videoTrack = newStream.getVideoTracks()[0];
      if (videoTrack) videoTrack.enabled = !isVideoMuted;

      setLocalStream(newStream);
      webrtcMeetingService.updateLocalStream(newStream);
    } catch (err: any) {
      console.warn('[Guest] Failed to switch microphone device:', err);
      setErrorMessage('Unable to activate selected microphone.');
    }
  };

  // Switch to next camera on Desktop PC
  const handleSwitchNextDesktopCamera = () => {
    if (videoDevices.length <= 1) return;
    const currentIndex = videoDevices.findIndex((d) => d.deviceId === selectedCameraId);
    const nextIndex = (currentIndex + 1) % videoDevices.length;
    handleSelectDesktopCamera(videoDevices[nextIndex].deviceId);
  };

  // Toggle Audio Mute
  const handleToggleAudio = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioMuted(!audioTrack.enabled);
        webrtcMeetingService.broadcastMediaState(!audioTrack.enabled, isVideoMuted);
      }
    }
  };

  // Toggle Video Off
  const handleToggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoMuted(!videoTrack.enabled);
        webrtcMeetingService.broadcastMediaState(isAudioMuted, !videoTrack.enabled);
      }
    }
  };

  // Connect via AnyDesk 6-digit code
  const handleJoinAnyDesk = async () => {
    const cleanCode = meetingCode.replace(/\D/g, '').trim();
    if (cleanCode.length !== 6) {
      setErrorMessage('Please enter a valid 6-digit Meeting ID.');
      return;
    }
    setErrorMessage(null);

    let stream = localStream;
    if (!stream) {
      try {
        stream = await acquireStream(facingMode, selectedCameraId, selectedMicId);
        setLocalStream(stream);
      } catch {
        setErrorMessage('Camera or Microphone access is required to join.');
        return;
      }
    }

    try {
      await webrtcMeetingService.joinGuestBroker(cleanCode, stream);
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to connect to host room.');
    }
  };

  // Connect via Manual Offer Code
  const handleGenerateAnswer = async () => {
    if (!inputOfferCode.trim()) {
      setErrorMessage('Please paste the Host Offer code.');
      return;
    }
    setErrorMessage(null);

    let stream = localStream;
    if (!stream) {
      try {
        stream = await acquireStream(facingMode, selectedCameraId, selectedMicId);
        setLocalStream(stream);
      } catch {
        setErrorMessage('Camera or Microphone access is required to join.');
        return;
      }
    }

    try {
      const answerCode = await webrtcMeetingService.acceptManualOffer(inputOfferCode.trim(), stream);
      setGeneratedAnswerCode(answerCode);
    } catch (err: any) {
      setErrorMessage(err.message || 'Invalid Host Offer code.');
    }
  };

  // Disconnect Call
  const handleDisconnect = () => {
    webrtcMeetingService.disconnect();
    setRemoteStream(null);
    setConsentGranted(false);
    setIsHostRecording(false);
    setConsentRequestModal(null);
    setIsAudioAutoplayBlocked(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const isConnected = connectionStatus === 'connected';

  // Current camera label for display
  const currentCameraLabel =
    videoDevices.find((d) => d.deviceId === selectedCameraId)?.label ||
    (videoDevices.length > 0 ? videoDevices[0].label : 'No Camera Detected');

  return (
    <div className="relative w-screen h-[100dvh] bg-[#0B1020] text-slate-100 flex flex-col overflow-hidden select-none font-sans">
      {/* Hidden Audio Element for Remote Host Sound */}
      <audio ref={remoteAudioRef} autoPlay playsInline />

      {/* --- TOP APP BAR --- */}
      <header className="shrink-0 px-3 sm:px-5 py-2 bg-[#11172A]/95 backdrop-blur-md border-b border-slate-800/80 flex items-center justify-between z-20 gap-2">
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md shadow-indigo-500/20 text-white shrink-0">
            <Radio className="w-3.5 h-3.5 animate-pulse" />
          </div>
          <div>
            <h1 className="text-xs sm:text-sm font-bold tracking-tight text-white flex items-center gap-1.5 leading-none">
              <span>MeetSplit AI</span>
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                Guest
              </span>
            </h1>
          </div>
        </div>

        {/* Tab Switcher in Center of Header (No second bar needed!) */}
        {onSwitchGuestTab && !isConnected ? (
          <div className="flex items-center p-0.5 rounded-xl bg-slate-900/90 border border-slate-800 shadow-inner">
            <button
              type="button"
              onClick={() => onSwitchGuestTab('group')}
              className="py-1 px-2.5 sm:px-3 rounded-lg font-bold text-[11px] sm:text-xs flex items-center justify-center gap-1.5 text-slate-400 hover:text-slate-200 transition-all cursor-pointer"
            >
              <Users className="w-3.5 h-3.5" />
              <span>Group Meeting</span>
            </button>
            <button
              type="button"
              onClick={() => onSwitchGuestTab('podcast')}
              className="py-1 px-2.5 sm:px-3 rounded-lg font-bold text-[11px] sm:text-xs flex items-center justify-center gap-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-sm cursor-pointer"
            >
              <Radio className="w-3.5 h-3.5" />
              <span>Podcast Guest</span>
            </button>
          </div>
        ) : !onSwitchGuestTab && !isConnected && (
          <button
            type="button"
            onClick={() => {
              const url = new URL(window.location.href);
              url.searchParams.set('mode', 'group-meeting');
              window.location.href = url.toString();
            }}
            className="px-2.5 py-1 rounded-lg bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 text-blue-300 font-bold text-[10px] sm:text-[11px] flex items-center gap-1 transition-all cursor-pointer"
          >
            <Users className="w-3.5 h-3.5" />
            <span>Switch to Group Meeting</span>
          </button>
        )}

        {/* Camera Switcher / Status & Install App in Header */}
        <div className="flex items-center gap-2">
          <PwaInstallButton variant="compact" />
          {isMobileDevice ? (
            <button
              type="button"
              onClick={handleFlipMobileCamera}
              disabled={isFlippingCamera}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-800/80 hover:bg-slate-700 active:scale-95 border border-slate-700 text-[11px] font-semibold text-slate-200 transition-all cursor-pointer shrink-0"
              title="Flip Camera (Front/Back)"
            >
              <SwitchCamera className={`w-3.5 h-3.5 text-indigo-400 ${isFlippingCamera ? 'animate-spin' : ''}`} />
              <span>{facingMode === 'user' ? 'Front' : 'Back'}</span>
            </button>
          ) : videoDevices.length > 1 ? (
            <button
              type="button"
              onClick={handleSwitchNextDesktopCamera}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-800/80 hover:bg-slate-700 active:scale-95 border border-slate-700 text-[11px] font-semibold text-slate-200 transition-all cursor-pointer shrink-0"
              title="Switch Webcam"
            >
              <Camera className="w-3.5 h-3.5 text-indigo-400" />
              <span className="truncate max-w-[110px] sm:max-w-[140px]">{currentCameraLabel}</span>
            </button>
          ) : (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-800/60 border border-slate-700/60 text-[10px] sm:text-[11px] text-slate-300 shrink-0">
              {videoDevices.length > 0 ? (
                <>
                  <Camera className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <span className="truncate max-w-[100px] sm:max-w-[140px] text-emerald-300 font-semibold">{currentCameraLabel}</span>
                </>
              ) : (
                <>
                  <Mic className="w-3.5 h-3.5 text-amber-400 animate-pulse shrink-0" />
                  <span className="truncate max-w-[100px] sm:max-w-[140px] text-amber-300 font-medium">Audio Only</span>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* --- ERROR NOTICE --- */}
      {errorMessage && (
        <div className="mx-3 mt-1.5 p-2 rounded-xl bg-red-500/15 border border-red-500/40 text-red-300 text-xs flex items-center justify-between shrink-0 z-30 animate-in fade-in max-w-4xl self-center w-full">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 text-red-400" />
            <span className="font-medium text-[11px]">{errorMessage}</span>
          </div>
          <button
            onClick={() => setErrorMessage(null)}
            className="text-[11px] font-bold underline hover:text-white ml-2 shrink-0 cursor-pointer"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* =========================================================================
          VIEW A: PRE-CALL JOIN INTERFACE (ZERO-SCROLL BALANCED STUDIO LOBBY)
         ========================================================================= */}
      {!isConnected ? (
        <main className="flex-1 min-h-0 overflow-hidden px-2.5 sm:px-6 py-2 flex flex-col justify-center items-center w-full max-w-5xl mx-auto scrollbar-none no-scrollbar">
          <div className="w-full grid grid-cols-1 sm:grid-cols-12 gap-2.5 sm:gap-4 lg:gap-6 items-center my-auto">
            {/* --- LEFT COLUMN: CAMERA PREVIEW + MEDIA HARDWARE SELECTORS --- */}
            <div className="sm:col-span-7 flex flex-col gap-2 justify-center">
              {/* 1. CAMERA PREVIEW CARD */}
              <div className="relative rounded-2xl bg-black border border-slate-800 shadow-xl overflow-hidden aspect-video sm:aspect-[16/10] max-h-[190px] sm:max-h-[280px] lg:max-h-[320px] flex items-center justify-center">
                {/* If permission denied or stream not available, show clear Grant Permission UI */}
                {!localStream || !hasPermission ? (
                  <div className="flex flex-col items-center justify-center p-4 text-center space-y-2 w-full h-full bg-slate-950">
                    <div className="w-10 h-10 rounded-xl bg-indigo-500/20 text-indigo-400 flex items-center justify-center border border-indigo-500/30">
                      <Camera className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-xs font-bold text-white">Camera & Microphone Access Needed</h3>
                      <p className="text-[11px] text-slate-400 mt-0.5 max-w-xs">
                        {permissionError || 'Please allow camera and microphone access to join the podcast studio.'}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-2 pt-0.5">
                      <button
                        type="button"
                        onClick={requestMediaPermissions}
                        className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-[11px] shadow-lg shadow-indigo-600/30 transition-all active:scale-95 cursor-pointer flex items-center gap-1.5"
                      >
                        <Camera className="w-3.5 h-3.5" />
                        <span>ALLOW CAMERA & MIC</span>
                      </button>
                      {typeof window !== 'undefined' && window.location.protocol === 'chrome-extension:' && (
                        <button
                          type="button"
                          onClick={() => {
                            const extChrome = typeof window !== 'undefined' ? (window as any).chrome : undefined;
                            if (extChrome?.tabs?.create) {
                              extChrome.tabs.create({ url: window.location.href });
                            } else {
                              window.open(window.location.href, '_blank');
                            }
                          }}
                          className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-semibold text-[11px] transition-all active:scale-95 cursor-pointer flex items-center gap-1.5"
                        >
                          <ExternalLink className="w-3.5 h-3.5 text-indigo-400" />
                          <span>Open in Chrome Tab</span>
                        </button>
                      )}
                    </div>
                  </div>
                ) : (!localStream.getVideoTracks().length || isVideoMuted) ? (
                  // Audio-Only Mode or Camera Off State
                  <div className="flex flex-col items-center justify-center p-4 text-center space-y-2 w-full h-full bg-slate-950/90 animate-in fade-in">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-tr from-purple-700 to-indigo-700 flex items-center justify-center text-xl sm:text-2xl font-black text-white shadow-xl shadow-purple-600/30 ring-4 ring-indigo-500/20">
                      {guestDisplayName.slice(0, 2).toUpperCase() || 'RG'}
                    </div>
                    <div>
                      <h3 className="text-xs sm:text-sm font-bold text-white flex items-center justify-center gap-1.5">
                        <Mic className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
                        <span>{videoDevices.length === 0 ? 'No Camera Detected (Audio-Only Mode)' : 'Camera is Turned Off'}</span>
                      </h3>
                      <p className="text-[10px] sm:text-xs text-slate-400 mt-0.5 max-w-xs">
                        {videoDevices.length === 0
                          ? 'Microphone is auto-detected and live. Connect a USB webcam anytime to enable video.'
                          : 'Your microphone is active. You can turn on camera whenever ready.'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <video
                      ref={previewVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover scale-x-[-1]"
                    />

                    {/* Top Status Pill */}
                    <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-black/60 backdrop-blur-md border border-white/10 text-[10px] text-white font-semibold">
                      <span className={`w-1.5 h-1.5 rounded-full ${videoDevices.length > 0 ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400 animate-pulse'}`} />
                      <span className="truncate max-w-[130px] sm:max-w-[160px]">
                        {isMobileDevice
                          ? facingMode === 'user'
                            ? 'Front Camera'
                            : 'Back Camera'
                          : currentCameraLabel}
                      </span>
                    </div>

                    {/* Bottom In-Preview Controls */}
                    <div className="absolute bottom-2.5 inset-x-0 flex items-center justify-center gap-2 sm:gap-3 z-10 px-3">
                      <button
                        type="button"
                        onClick={handleToggleAudio}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold shadow-lg backdrop-blur-md transition-all active:scale-95 cursor-pointer ${
                          isAudioMuted
                            ? 'bg-red-600/90 text-white border border-red-500'
                            : 'bg-black/60 hover:bg-black/80 text-emerald-300 border border-emerald-500/40'
                        }`}
                      >
                        {isAudioMuted ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                        <span>{isAudioMuted ? 'Muted' : 'Mic Live'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={handleToggleVideo}
                        disabled={videoDevices.length === 0}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold shadow-lg backdrop-blur-md transition-all active:scale-95 cursor-pointer ${
                          videoDevices.length === 0
                            ? 'bg-slate-800/80 text-slate-500 border border-slate-700 cursor-not-allowed'
                            : isVideoMuted
                            ? 'bg-red-600/90 text-white border border-red-500'
                            : 'bg-black/60 hover:bg-black/80 text-indigo-300 border border-indigo-500/40'
                        }`}
                      >
                        {isVideoMuted ? <VideoOff className="w-3.5 h-3.5" /> : <Video className="w-3.5 h-3.5" />}
                        <span>{videoDevices.length === 0 ? 'No Camera' : isVideoMuted ? 'Cam Off' : 'Cam Live'}</span>
                      </button>

                      {/* Camera Switcher button */}
                      {isMobileDevice ? (
                        <button
                          type="button"
                          onClick={handleFlipMobileCamera}
                          disabled={isFlippingCamera}
                          className="p-1.5 rounded-xl bg-black/60 hover:bg-black/80 border border-white/15 text-white active:scale-95 transition-all cursor-pointer"
                          title="Flip Camera (Front/Back)"
                        >
                          <SwitchCamera className={`w-3.5 h-3.5 text-white ${isFlippingCamera ? 'animate-spin' : ''}`} />
                        </button>
                      ) : (
                        videoDevices.length > 1 && (
                          <button
                            type="button"
                            onClick={handleSwitchNextDesktopCamera}
                            className="p-1.5 rounded-xl bg-black/60 hover:bg-black/80 border border-white/15 text-white active:scale-95 transition-all cursor-pointer"
                            title="Switch to next camera"
                          >
                            <Camera className="w-3.5 h-3.5 text-white" />
                          </button>
                        )
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* 1.1 DESKTOP DEVICE SELECTORS (Auto-Detected Media Hardware - Desktop PC Only) */}
              {!isMobileDevice && (
                <div className="grid grid-cols-2 gap-2 p-2 rounded-xl bg-slate-900/90 border border-slate-800 text-xs shadow-md">
                  {/* Webcam Selector */}
                  <div className="space-y-0.5">
                    <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        <Camera className="w-3 h-3 text-indigo-400" />
                        <span>Webcam</span>
                      </span>
                      <span className={`text-[8px] font-mono px-1 py-0.2 rounded ${videoDevices.length > 0 ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20' : 'text-amber-400 bg-amber-500/10 border border-amber-500/20'}`}>
                        {videoDevices.length > 0 ? `${videoDevices.length} Auto` : 'None'}
                      </span>
                    </label>
                    <select
                      value={selectedCameraId}
                      onChange={(e) => handleSelectDesktopCamera(e.target.value)}
                      disabled={videoDevices.length === 0}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-[11px] text-slate-200 focus:outline-none focus:border-indigo-500 truncate cursor-pointer disabled:opacity-50"
                    >
                      {videoDevices.length === 0 ? (
                        <option value="">No Camera Detected</option>
                      ) : (
                        videoDevices.map((d) => (
                          <option key={d.deviceId} value={d.deviceId}>
                            {d.label || 'Webcam'}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  {/* Microphone Selector */}
                  <div className="space-y-0.5">
                    <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        <Mic className="w-3 h-3 text-emerald-400" />
                        <span>Microphone</span>
                      </span>
                      <span className={`text-[8px] font-mono px-1 py-0.2 rounded ${audioDevices.length > 0 ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20' : 'text-rose-400 bg-rose-500/10 border border-rose-500/20'}`}>
                        {audioDevices.length > 0 ? `${audioDevices.length} Auto` : 'None'}
                      </span>
                    </label>
                    <select
                      value={selectedMicId}
                      onChange={(e) => handleSelectDesktopMic(e.target.value)}
                      disabled={audioDevices.length === 0}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-[11px] text-slate-200 focus:outline-none focus:border-indigo-500 truncate cursor-pointer disabled:opacity-50"
                    >
                      {audioDevices.length === 0 ? (
                        <option value="">No Microphone Detected</option>
                      ) : (
                        audioDevices.map((d) => (
                          <option key={d.deviceId} value={d.deviceId}>
                            {d.label || 'Microphone'}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* --- RIGHT COLUMN: JOIN MEETING CARD + SECURITY BANNER --- */}
            <div className="sm:col-span-5 flex flex-col gap-2 justify-center">
              {/* 2. JOIN MEETING CARD */}
              <div className="p-3 sm:p-4 rounded-2xl bg-slate-900/90 border border-slate-800 shadow-xl space-y-2.5">
                {/* Mode Tabs */}
                <div className="flex items-center p-0.5 rounded-xl bg-slate-800/80 border border-slate-700/60">
                  <button
                    type="button"
                    onClick={() => setSignalingMode('anydesk')}
                    className={`flex-1 py-1 text-[11px] font-bold rounded-lg transition-all text-center cursor-pointer ${
                      signalingMode === 'anydesk'
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    ⚡ 6-Digit Meeting ID
                  </button>
                  <button
                    type="button"
                    onClick={() => setSignalingMode('manual')}
                    className={`flex-1 py-1 text-[11px] font-bold rounded-lg transition-all text-center cursor-pointer ${
                      signalingMode === 'manual'
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    📋 QR / Offer Code
                  </button>
                </div>

                {/* --- TAB A: 6-DIGIT CODE --- */}
                {signalingMode === 'anydesk' && (
                  <div className="space-y-2">
                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 block mb-0.5">
                        Enter Host's 6-Digit Code
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={meetingCode}
                        onChange={(e) => setMeetingCode(e.target.value.replace(/\D/g, ''))}
                        placeholder="e.g. 582914"
                        className="w-full px-3 py-1.5 sm:py-2 rounded-xl bg-slate-800/90 border-2 border-indigo-500/40 focus:border-indigo-500 text-center text-xl sm:text-2xl font-black font-mono tracking-widest text-white focus:outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 block mb-0.5">
                        Your Display Name
                      </label>
                      <input
                        type="text"
                        value={guestDisplayName}
                        onChange={(e) => setGuestDisplayName(e.target.value)}
                        placeholder="Guest / Your Name"
                        className="w-full px-3 py-1.5 rounded-lg bg-slate-800/70 border border-slate-700/80 text-xs font-semibold text-slate-200 focus:outline-none focus:border-indigo-500"
                      />
                    </div>

                    {/* Status Indicator */}
                    {connectionStatus === 'connecting' && (
                      <div className="flex items-center gap-2 p-2 rounded-xl bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 text-[11px] animate-pulse">
                        <div className="w-2 h-2 rounded-full bg-indigo-400 animate-ping" />
                        <span>Connecting to Host Studio...</span>
                      </div>
                    )}

                    {/* Big Join Button */}
                    <button
                      type="button"
                      onClick={handleJoinAnyDesk}
                      disabled={meetingCode.replace(/\D/g, '').length !== 6 || connectionStatus === 'connecting'}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 via-indigo-500 to-purple-600 hover:from-indigo-500 hover:to-purple-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-xs uppercase tracking-wider shadow-lg shadow-indigo-600/30 active:scale-[0.98] transition-all cursor-pointer"
                    >
                      <PhoneCall className="w-4 h-4" />
                      <span>
                        {connectionStatus === 'connecting' ? 'CONNECTING...' : 'JOIN PODCAST STUDIO'}
                      </span>
                    </button>
                  </div>
                )}

                {/* --- TAB B: MANUAL / QR OFFER --- */}
                {signalingMode === 'manual' && (
                  <div className="space-y-2">
                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 block mb-0.5">
                        Step 1: Paste Host's Offer Code
                      </label>
                      <textarea
                        rows={2}
                        value={inputOfferCode}
                        onChange={(e) => setInputOfferCode(e.target.value)}
                        placeholder="Paste Offer Code from Host..."
                        className="w-full px-2.5 py-1.5 rounded-lg bg-slate-800/90 border border-slate-700 text-xs font-mono text-slate-200 focus:outline-none focus:border-indigo-500"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={handleGenerateAnswer}
                      disabled={!inputOfferCode.trim() || connectionStatus === 'connecting'}
                      className="w-full py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-bold text-xs shadow-md transition-all active:scale-95 cursor-pointer"
                    >
                      GENERATE ANSWER CODE
                    </button>

                    {generatedAnswerCode && (
                      <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 space-y-1">
                        <label className="text-[9px] font-bold uppercase tracking-wider text-emerald-400 block">
                          Step 2: Copy & Send to Host
                        </label>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="text"
                            readOnly
                            value={generatedAnswerCode}
                            className="flex-1 px-2 py-1 rounded bg-slate-900 border border-slate-700 text-[11px] font-mono text-slate-300 truncate"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(generatedAnswerCode);
                              setCopiedAnswer(true);
                              setTimeout(() => setCopiedAnswer(false), 2000);
                            }}
                            className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs font-bold text-white flex items-center gap-1 cursor-pointer"
                          >
                            {copiedAnswer ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                            <span>{copiedAnswer ? 'Copied' : 'Copy'}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 3. ZERO STORAGE & PRIVACY BANNER */}
              <div className="p-2 rounded-xl bg-slate-900/60 border border-slate-800/80 text-[11px] flex items-center gap-2">
                <div className="p-1 rounded-lg bg-emerald-500/15 text-emerald-400 shrink-0">
                  <ShieldCheck className="w-3.5 h-3.5" />
                </div>
                <div className="text-slate-300 text-[10px] sm:text-[11px] leading-tight">
                  <strong className="text-white block font-semibold">Zero Storage Needed on Your Device</strong>
                  <span className="text-slate-400">Direct P2P WebRTC. Video & audio recorded safely on Host PC.</span>
                </div>
              </div>
            </div>
          </div>
        </main>
      ) : (
        /* =========================================================================
            VIEW B: IN-CALL CONNECTED STAGE (RESPONSIVE FOR DESKTOP & MOBILE)
           ========================================================================= */
        <div className="flex-1 min-h-0 flex flex-col justify-between p-2.5 sm:p-4 space-y-2 sm:space-y-3 overflow-hidden">
          {/* Top In-Call Info Header */}
          <div className="shrink-0 px-3 py-2 rounded-xl bg-slate-900/90 border border-slate-800 flex items-center justify-between gap-2 shadow-md">
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Live P2P
              </span>

              <div className="flex items-center gap-1 text-xs text-slate-400 font-mono">
                <Clock className="w-3.5 h-3.5 text-indigo-400" />
                <span className="text-white font-semibold">{formatDuration(callDurationSec)}</span>
              </div>

              {rttLatencyMs !== null && (
                <span className="text-[10px] font-mono text-slate-400 hidden sm:inline">
                  RTT: <strong className="text-white">{rttLatencyMs}ms</strong>
                </span>
              )}
            </div>

            {/* Audio Unlock Button if Autoplay was blocked */}
            {isAudioAutoplayBlocked && (
              <button
                type="button"
                onClick={() => {
                  if (remoteAudioRef.current) {
                    remoteAudioRef.current.play();
                    setIsAudioAutoplayBlocked(false);
                  }
                }}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-500 text-black font-bold text-xs animate-bounce cursor-pointer"
              >
                <Volume2 className="w-3.5 h-3.5" />
                <span>Tap to Unmute</span>
              </button>
            )}

            {/* Disconnect Call Button */}
            <button
              type="button"
              onClick={handleDisconnect}
              className="flex items-center gap-1 px-3 py-1 rounded-xl bg-red-600/90 hover:bg-red-600 text-white font-bold text-xs shadow-md transition-all active:scale-95 cursor-pointer"
            >
              <PhoneOff className="w-3.5 h-3.5" />
              <span>Leave</span>
            </button>
          </div>

          {/* Recording Notice Pill */}
          {isHostRecording ? (
            <div className="shrink-0 px-3 py-1.5 rounded-xl bg-red-500/15 border border-red-500/30 text-red-300 text-xs flex items-center gap-2 justify-center font-semibold animate-pulse">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-ping" />
              <span>🎙️ Host is recording this podcast episode on Host PC</span>
            </div>
          ) : (
            <div className="shrink-0 px-3 py-1 rounded-xl bg-slate-900/60 border border-slate-800 text-[11px] text-slate-400 flex items-center gap-2 justify-center">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span>P2P Studio Stream Active • 1080p WebRTC Direct</span>
            </div>
          )}

          {/* Central Stage: Dominant View + PIP Swap Tile */}
          <div className="relative flex-1 min-h-0 rounded-2xl bg-black border border-slate-800 overflow-hidden shadow-2xl flex items-center justify-center">
            {/* Primary Video Tile */}
            <div className="relative w-full h-full flex items-center justify-center bg-neutral-950">
              <video
                ref={primaryView === 'remote' ? hostVideoRef : guestVideoRef}
                autoPlay
                playsInline
                muted={primaryView === 'local'}
                className={`w-full h-full object-cover ${
                  primaryView === 'local' ? 'scale-x-[-1]' : ''
                }`}
              />

              {/* Top Label for Primary Tile */}
              <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 rounded-xl bg-black/60 backdrop-blur-md border border-white/10 text-xs text-white">
                <span
                  className={`w-2 h-2 rounded-full ${
                    primaryView === 'remote' ? 'bg-purple-500' : 'bg-emerald-500'
                  }`}
                />
                <span className="font-semibold">
                  {primaryView === 'remote' ? `${hostDisplayName} (Host)` : `${guestDisplayName} (You)`}
                </span>
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/10 font-mono text-slate-300">
                  1080p
                </span>
              </div>

              {/* Mute status on primary */}
              <div className="absolute bottom-3 left-3 flex items-center gap-1.5">
                {(primaryView === 'remote' ? remoteMediaState.audioMuted : isAudioMuted) && (
                  <span className="p-1.5 rounded-lg bg-red-600/90 text-white" title="Mic Muted">
                    <MicOff className="w-3.5 h-3.5" />
                  </span>
                )}
                {(primaryView === 'remote' ? remoteMediaState.videoOff : isVideoMuted) && (
                  <span className="p-1.5 rounded-lg bg-red-600/90 text-white" title="Camera Off">
                    <VideoOff className="w-3.5 h-3.5" />
                  </span>
                )}
              </div>
            </div>

            {/* PIP Thumbnail Tile (Tap to Swap Primary) */}
            <div
              onClick={() => setPrimaryView(primaryView === 'remote' ? 'local' : 'remote')}
              className="absolute top-3 right-3 w-32 sm:w-44 aspect-[4/3] rounded-xl overflow-hidden bg-black border-2 border-indigo-500/70 shadow-2xl z-10 cursor-pointer active:scale-95 transition-transform"
              title="Tap to swap screens"
            >
              <video
                ref={primaryView === 'remote' ? guestVideoRef : hostVideoRef}
                autoPlay
                playsInline
                muted
                className={`w-full h-full object-cover ${
                  primaryView === 'remote' ? 'scale-x-[-1]' : ''
                }`}
              />
              <div className="absolute bottom-1 inset-x-1 px-1 py-0.5 rounded bg-black/70 text-[9px] text-white text-center font-bold truncate">
                {primaryView === 'remote' ? 'You (Tap to swap)' : `${hostDisplayName}`}
              </div>
            </div>
          </div>

          {/* Floating In-Call Dock */}
          <div className="shrink-0 p-3 rounded-2xl bg-slate-900/95 border border-slate-800 shadow-2xl flex items-center justify-around gap-2 z-20 max-w-lg mx-auto w-full">
            <button
              type="button"
              onClick={handleToggleAudio}
              className={`flex flex-col items-center gap-1 p-2.5 rounded-xl text-xs font-bold transition-all active:scale-95 cursor-pointer ${
                isAudioMuted ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
              }`}
            >
              {isAudioMuted ? <MicOff className="w-5 h-5 text-white" /> : <Mic className="w-5 h-5 text-emerald-400" />}
              <span className="text-[10px]">{isAudioMuted ? 'Unmute' : 'Mute'}</span>
            </button>

            <button
              type="button"
              onClick={handleToggleVideo}
              className={`flex flex-col items-center gap-1 p-2.5 rounded-xl text-xs font-bold transition-all active:scale-95 cursor-pointer ${
                isVideoMuted ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
              }`}
            >
              {isVideoMuted ? <VideoOff className="w-5 h-5 text-white" /> : <Video className="w-5 h-5 text-indigo-400" />}
              <span className="text-[10px]">{isVideoMuted ? 'Start Cam' : 'Stop Cam'}</span>
            </button>

            {/* In-Call Camera Action: Mobile shows Flip Cam, Desktop shows Switch Cam if multiple */}
            {isMobileDevice ? (
              <button
                type="button"
                onClick={handleFlipMobileCamera}
                disabled={isFlippingCamera}
                className="flex flex-col items-center gap-1 p-2.5 rounded-xl text-xs font-bold bg-slate-800 text-slate-200 hover:bg-slate-700 active:scale-95 transition-all cursor-pointer"
              >
                <SwitchCamera className={`w-5 h-5 text-purple-400 ${isFlippingCamera ? 'animate-spin' : ''}`} />
                <span className="text-[10px]">Flip Cam</span>
              </button>
            ) : videoDevices.length > 1 ? (
              <button
                type="button"
                onClick={handleSwitchNextDesktopCamera}
                className="flex flex-col items-center gap-1 p-2.5 rounded-xl text-xs font-bold bg-slate-800 text-slate-200 hover:bg-slate-700 active:scale-95 transition-all cursor-pointer"
              >
                <Camera className="w-5 h-5 text-purple-400" />
                <span className="text-[10px]">Switch Cam</span>
              </button>
            ) : null}

            <button
              type="button"
              onClick={handleDisconnect}
              className="flex flex-col items-center gap-1 p-2.5 rounded-xl text-xs font-bold bg-red-600 hover:bg-red-700 text-white active:scale-95 transition-all cursor-pointer"
            >
              <PhoneOff className="w-5 h-5 text-white" />
              <span className="text-[10px]">End Call</span>
            </button>
          </div>
        </div>
      )}

      {/* --- RECORDING CONSENT MODAL --- */}
      {consentRequestModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-sm rounded-2xl bg-slate-900 border border-slate-700 p-5 shadow-2xl space-y-4 animate-in fade-in zoom-in-95">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-purple-500/20 text-purple-400">
                <Radio className="w-6 h-6 animate-pulse" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">Recording Consent Request</h3>
                <p className="text-xs text-slate-400 font-medium">Podcast Studio Pro</p>
              </div>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed">
              <strong>{consentRequestModal.hostName}</strong> wants to record your audio and video stream for this podcast episode.
            </p>

            <div className="p-3 rounded-xl bg-slate-800/80 border border-slate-700 text-[11px] text-slate-400 space-y-1">
              <p>• Saved exclusively on Host PC</p>
              <p>• Zero disk space used on your device</p>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  webrtcMeetingService.respondToRecordingConsent(false);
                  setConsentRequestModal(null);
                }}
                className="flex-1 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold active:scale-95 cursor-pointer"
              >
                Decline
              </button>
              <button
                type="button"
                onClick={() => {
                  webrtcMeetingService.respondToRecordingConsent(true);
                  setConsentGranted(true);
                  setConsentRequestModal(null);
                }}
                className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shadow-lg shadow-emerald-600/30 active:scale-95 cursor-pointer"
              >
                Allow Recording
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
